/**
 * SettingsLayout — inner layout wrapping all /admin/settings/* pages.
 *
 * Renders:
 *   Left column (desktop):  Fixed settings navigation sidebar with grouped items.
 *   Top row (mobile):       Horizontal scrollable tab bar.
 *   Right column:           <Outlet /> — the active settings page.
 *
 * Follows the same animation, color-token, and routing conventions as MainLayout.
 */

import { Link, useLocation, Outlet } from 'react-router-dom'
import { motion }                    from 'framer-motion'
import {
  UserCog, ShieldCheck, Building2, Palette,
  Globe2, Bell, Lock,
} from 'lucide-react'

import { ROUTES } from '@constants/routes'
import { cn }     from '@utils'

// ── Nav config ────────────────────────────────────────────────────────────────

const NAV_GROUPS = [
  {
    label: 'Account',
    items: [
      { href: ROUTES.ADMIN.SETTINGS.PROFILE,  icon: UserCog,   label: 'Profile'          },
      { href: ROUTES.ADMIN.SETTINGS.ACCOUNT,  icon: ShieldCheck, label: 'Account & Password' },
    ],
  },
  {
    label: 'Workspace',
    items: [
      { href: ROUTES.ADMIN.SETTINGS.ORGANIZATION, icon: Building2, label: 'Organization' },
      { href: ROUTES.ADMIN.SETTINGS.BRANDING,     icon: Palette,   label: 'Branding'     },
      { href: ROUTES.ADMIN.SETTINGS.REGIONAL,     icon: Globe2,    label: 'Regional'     },
    ],
  },
  {
    label: 'System',
    items: [
      { href: ROUTES.ADMIN.SETTINGS.NOTIFICATIONS, icon: Bell, label: 'Notifications' },
      { href: ROUTES.ADMIN.SETTINGS.SECURITY,      icon: Lock, label: 'Security'      },
    ],
  },
]

const ALL_ITEMS = NAV_GROUPS.flatMap((g) => g.items)

// ── Sidebar NavItem ───────────────────────────────────────────────────────────

function NavItem({ item, pathname }) {
  const isActive = pathname === item.href || pathname.startsWith(item.href + '/')
  const Icon = item.icon

  return (
    <Link
      to={item.href}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'relative flex items-center gap-3 h-9 px-3 rounded-xl text-sm font-medium',
        'transition-colors duration-150 outline-none',
        'focus-visible:ring-2 focus-visible:ring-[var(--ac-secondary)] focus-visible:ring-inset',
        isActive
          ? 'text-[var(--ac-secondary)] bg-[var(--ac-secondary)]/10'
          : 'text-[var(--ac-text-muted)] hover:bg-[var(--ac-bg-subtle)] hover:text-[var(--ac-text-base)]',
      )}
    >
      {isActive && (
        <span
          className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 rounded-r-full"
          style={{ background: 'var(--ac-secondary)' }}
        />
      )}
      <Icon size={15} strokeWidth={isActive ? 2 : 1.75} className="flex-shrink-0" />
      <span className="truncate">{item.label}</span>
    </Link>
  )
}

// ── Mobile tab bar ────────────────────────────────────────────────────────────

function MobileTabBar({ pathname }) {
  return (
    <div
      className="lg:hidden flex gap-1 overflow-x-auto pb-1 mb-5 scrollbar-none"
      style={{ borderBottom: '1px solid var(--ac-border-base)' }}
    >
      {ALL_ITEMS.map((item) => {
        const isActive = pathname === item.href || pathname.startsWith(item.href + '/')
        const Icon = item.icon
        return (
          <Link
            key={item.href}
            to={item.href}
            aria-current={isActive ? 'page' : undefined}
            className={cn(
              'flex items-center gap-1.5 px-3 h-9 rounded-t-xl text-xs font-medium flex-shrink-0 border-b-2 transition-colors duration-150',
              isActive
                ? 'border-[var(--ac-secondary)] text-[var(--ac-secondary)]'
                : 'border-transparent text-[var(--ac-text-muted)] hover:text-[var(--ac-text-base)]',
            )}
          >
            <Icon size={13} strokeWidth={isActive ? 2 : 1.75} />
            {item.label}
          </Link>
        )
      })}
    </div>
  )
}

// ── Main ─────────────────────────────────────────────────────────────────────

export function SettingsLayout() {
  const { pathname } = useLocation()

  return (
    <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">

      {/* ── Desktop sidebar ───────────────────────────────────────── */}
      <motion.aside
        className="hidden lg:flex flex-col w-52 flex-shrink-0"
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x:   0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
      >
        {/* Sticky inner nav */}
        <div className="sticky top-20 space-y-4">
          <div>
            <p
              className="text-xs font-bold uppercase tracking-widest mb-2 px-3"
              style={{ color: 'var(--ac-text-subtle)', letterSpacing: '0.1em' }}
            >
              Settings
            </p>
          </div>

          {NAV_GROUPS.map((group) => (
            <div key={group.label} className="space-y-0.5">
              <p
                className="px-3 pb-1 text-xs font-semibold uppercase tracking-wider"
                style={{ color: 'var(--ac-text-subtle)', letterSpacing: '0.07em' }}
              >
                {group.label}
              </p>
              {group.items.map((item) => (
                <NavItem key={item.href} item={item} pathname={pathname} />
              ))}
            </div>
          ))}
        </div>
      </motion.aside>

      {/* ── Content area ──────────────────────────────────────────── */}
      <div className="flex-1 min-w-0">
        {/* Mobile tabs */}
        <MobileTabBar pathname={pathname} />

        {/* Page */}
        <motion.div
          key={pathname}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y:  0 }}
          transition={{ duration: 0.28, ease: 'easeOut' }}
        >
          <Outlet />
        </motion.div>
      </div>
    </div>
  )
}
